import { Avatar } from "@/src/components/account/Avatar";
import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import Loading from "@/src/components/Loading";
import { Button } from "@/src/components/tailwind/Button";
import Dropdown, { Option } from "@/src/components/tailwind/Dropdown";
import { toasts } from "@/src/components/toasts/toasts";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import {
  AccessControlAddMoreQuery,
  AccessControlDealGroup,
  PermissionOverrideEntityType,
  PermissionsResourceType,
  useAccessControlAddMoreQuery,
  useCreateDealRoleOverridesMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { classNames } from "@/src/utils/cn";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { dealRoleToDealRoleOption } from "@/src/utils/typeconv";

interface AddMoreModalProps {
  open: boolean;
  onClose: () => void;

  dealId: string;
  resourceId: string;
  access: AccessControlDealGroup[];
  resourceType: PermissionsResourceType;
  title?: string;
}

export function AddMoreModal(props: AddMoreModalProps) {
  const { open, dealId, resourceId, access, resourceType } = props;

  const client = useGqlClient();
  const addMoreQuery = useAccessControlAddMoreQuery(client, {
    dealId,
    dealRolesInput: {
      roleTypes: [],
      resourceTypes: [props.resourceType],
    },
  });

  function onClose() {
    props.onClose();
  }

  return (
    <AnimatedModal open={open} onClose={onClose} size="xl" padding="0">
      <div className="flex text-md justify-between items-center p-4">
        <p className="font-semibold text-gray-700">
          {props.title ? props.title : "Manage group access"}
        </p>
        <CloseIcon onClose={onClose} />
      </div>
      <AddMoreModalContent
        access={access}
        addMoreQuery={addMoreQuery}
        resourceId={resourceId}
        dealId={dealId}
        onClose={onClose}
        resourceType={resourceType}
      />
    </AnimatedModal>
  );
}

interface SelectedGroup {
  id: string;
  dealRoleId: string;
}

function AddMoreModalContent(props: {
  access: AccessControlDealGroup[];
  addMoreQuery: UseQueryResult<AccessControlAddMoreQuery, unknown>;
  resourceId: string;
  resourceType: PermissionsResourceType;
  dealId: string;
  onClose: () => void;
}) {
  const { addMoreQuery, dealId, onClose } = props;
  const [changedGroups, setChangedGroups] = useState<SelectedGroup[]>([]);
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createDealRoleOverride = useCreateDealRoleOverridesMutation(client);
  const [options, setOptions] = useState<Option[]>([]);

  useEffect(() => {
    if (!addMoreQuery.data || options.length > 0) {
      return;
    }

    const roleOptions = addMoreQuery.data.dealRoles.map((dr) =>
      dealRoleToDealRoleOption(dr),
    );

    setOptions(roleOptions);
  }, [addMoreQuery.data]);

  if (addMoreQuery.error) {
    return (
      <div>
        <p className="text-sm text-gray-500">Something went wrong</p>
      </div>
    );
  }

  if (addMoreQuery.isLoading || !addMoreQuery.data) {
    return <Loading />;
  }

  const guestGroups = addMoreQuery.data.deal.guestGroups;
  const internalGroups = addMoreQuery.data.deal.ownerGroups;

  // Helper function to get existing access level for a group
  const getExistingAccess = (groupId: string) => {
    return props.access.find((access) => access.dealGroup.id === groupId);
  };

  // Initialize changedGroups when data is loaded
  useEffect(() => {
    if (addMoreQuery.data) {
      const allGroups = [...internalGroups, ...guestGroups];
      const initialChangedGroups: SelectedGroup[] = allGroups.map((group) => {
        const existingAccess = getExistingAccess(group.id);
        return {
          id: group.id,
          dealRoleId: existingAccess ? existingAccess.dealRole.id : "",
        };
      });
      setChangedGroups(initialChangedGroups);
    }
  }, [addMoreQuery.data, internalGroups, guestGroups, props.access]);

  return (
    <div className="">
      <div className="overflow-hidden max-h-96 overflow-y-auto">
        <table className="min-w-full table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-48" />
          </colgroup>
          <tbody className="bg-white ">
            {/* Internal Groups Section */}
            {internalGroups.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-2 bg-gray-50 text-sm font-semibold text-gray-700 border-y"
                  >
                    Internal Groups
                  </td>
                </tr>
                {internalGroups.map((group) => (
                  <DealGroupTableRow
                    key={group.id}
                    group={group}
                    changedGroups={changedGroups}
                    setChangedGroups={setChangedGroups}
                    options={options}
                    existingAccess={getExistingAccess(group.id)}
                    isInternal={true}
                  />
                ))}
              </>
            )}

            {/* Guest Groups Section */}
            {guestGroups.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={2}
                    className="px-4 py-2 bg-gray-50 text-sm font-semibold text-gray-700 border-y"
                  >
                    Guest Groups
                  </td>
                </tr>
                {guestGroups.map((group) => (
                  <DealGroupTableRow
                    key={group.id}
                    group={group}
                    changedGroups={changedGroups}
                    setChangedGroups={setChangedGroups}
                    options={options}
                    existingAccess={getExistingAccess(group.id)}
                    isInternal={false}
                  />
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end p-4 mt-3 border-t">
        <Button
          variant="positive"
          text="Update access"
          isDisabled={
            changedGroups.filter((group) => {
              // Skip internal groups since they can't be modified
              const isInternalGroup = internalGroups.some(
                (ig) => ig.id === group.id,
              );
              if (isInternalGroup) return false;

              const existingAccess = getExistingAccess(group.id);
              return group.dealRoleId !== existingAccess?.dealRole.id;
            }).length === 0 || createDealRoleOverride.isPending
          }
          isLoading={createDealRoleOverride.isPending}
          loadingText="Updating access..."
          onClick={() => {
            const actualChanges = changedGroups.filter((group) => {
              // Skip internal groups since they can't be modified
              const isInternalGroup = internalGroups.some(
                (ig) => ig.id === group.id,
              );
              if (isInternalGroup) return false;

              const existingAccess = getExistingAccess(group.id);

              return (
                group.dealRoleId !== "" &&
                group.dealRoleId !== existingAccess?.dealRole.id
              );
            });

            if (
              actualChanges.length === 0 ||
              createDealRoleOverride.isPending
            ) {
              return;
            }

            createDealRoleOverride.mutate(
              {
                input: actualChanges.map((group) => {
                  return {
                    dealRoleID: group.dealRoleId,
                    entityID: group.id,
                    entityType: PermissionOverrideEntityType.DealGroup,
                    resourceID: props.resourceId,
                    resourceType: props.resourceType,
                  };
                }),
              },
              {
                onError: () => {
                  toasts.error("Failed to update access");
                },
                onSuccess: () => {
                  toasts.success("Access updated");
                  queryClient.invalidateQueries({
                    queryKey: ["AccessControlAddMore", { dealId }],
                  });
                  props.onClose();
                },
              },
            );
          }}
        />
      </div>
    </div>
  );
}

function DealGroupTableRow(props: {
  options: Option[];
  setChangedGroups: (groups: SelectedGroup[]) => void;
  changedGroups: SelectedGroup[];
  group:
    | AccessControlAddMoreQuery["deal"]["guestGroups"][0]
    | AccessControlAddMoreQuery["deal"]["ownerGroups"][0];
  existingAccess?: AccessControlDealGroup;
  isInternal: boolean;
}) {
  const currentGroup = props.changedGroups.find((g) => g.id === props.group.id);
  const [selectedOption, setSelectedOption] = useState<Option | undefined>(
    currentGroup
      ? props.options.find((opt) => opt.value === currentGroup.dealRoleId)
      : undefined,
  );
  const [isExpanded, setIsExpanded] = useState(false);

  const hasExistingAccess = !!props.existingAccess;

  // Sync selectedOption with changedGroups when they're initialized (only for guest groups)
  useEffect(() => {
    if (props.isInternal) return;

    const currentGroup = props.changedGroups.find(
      (g) => g.id === props.group.id,
    );
    if (currentGroup && !selectedOption) {
      const option = props.options.find(
        (opt) => opt.value === currentGroup.dealRoleId,
      );
      if (option) {
        setSelectedOption(option);
      }
    }
  }, [
    props.changedGroups,
    props.group.id,
    props.options,
    selectedOption,
    props.isInternal,
  ]);

  useEffect(() => {
    if (props.isInternal || !selectedOption) {
      return;
    }

    // Update changedGroups when dropdown selection changes (only for guest groups)
    props.setChangedGroups(
      props.changedGroups.filter((g) => g.id !== props.group.id),
    );

    props.setChangedGroups([
      ...props.changedGroups.filter((g) => g.id !== props.group.id),
      {
        id: props.group.id,
        dealRoleId: selectedOption.value ?? "",
      },
    ]);
  }, [selectedOption, props.isInternal]);

  return (
    <>
      <tr
        className={classNames(
          "hover:bg-gray-50",
          hasExistingAccess ? "bg-blue-50/30" : "",
        )}
      >
        <td className="px-4 py-4 ">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center justify-center w-5 h-5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4" />
              ) : (
                <ChevronRightIcon className="w-4 h-4" />
              )}
            </button>
            <div>
              <p className="text-sm font-semibold text-gray-700">
                {props.group.name}
                <span className="ml-1 text-sm text-gray-500/80 font-normal">
                  ({props.group.accounts.length} people)
                </span>
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-4 whitespace-nowrap text-right">
          {props.isInternal ? (
            <span className="text-sm text-gray-700 font-medium">Admin</span>
          ) : (
            <div className="flex justify-end">
              <Dropdown
                hideSelectedOptionDescription
                selectedOption={selectedOption}
                options={props.options}
                onSelect={(option) => {
                  setSelectedOption(option);
                }}
                size="s"
              />
            </div>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={2} className="px-4 py-3">
            <div className="ml-7">
              <div className="space-y-2">
                {props.group.accounts.map((account) => (
                  <div key={account.id} className="flex items-center gap-3">
                    <Avatar size="s" account={account.account} />
                    <span className="text-sm text-gray-700">
                      {account.account.name}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
